import { Inject, Injectable, InjectionToken, NgZone, OnDestroy } from '@angular/core';
import { UserSignalrService } from '@app/shared/layout/change-status/user-signalr.service';
import { AppSessionService } from '@shared/common/session/app-session.service';
import {
    ConversationCountsDto,
    ConversationReadStateResultDto,
    ConversationServiceProxy,
    ConversationTabCountsDto,
    GetConversationCountsInput,
} from '@shared/service-proxies/service-proxies';
import { PermissionCheckerService } from 'abp-ng2-module';
import { asyncScheduler, BehaviorSubject, defer, EMPTY, merge, Observable, SchedulerLike, Subject, timer } from 'rxjs';
import {
    auditTime,
    catchError,
    distinctUntilChanged,
    exhaustMap,
    filter,
    finalize,
    map,
    share,
    shareReplay,
    switchMap,
    takeUntil,
    tap,
} from 'rxjs/operators';
import { ConversationBadgeVm, toBadgeVm } from './conversation-badge';
import {
    answerProjectIds,
    applyBadgeDelta,
    badgeFromAnswer,
    breakerOpen,
    chunkIds,
    COUNTS_AUDIT_MS,
    COUNTS_SETTLE_MS,
    CountsAnswerKind,
    CountsAnswerPlan,
    CountsAnswerStamp,
    CountsAnswerVerdict,
    CountsAsOfMarks,
    CountsOwner,
    CountsSequenceState,
    droppedAnswerNeedsReconcile,
    feedProjectCounts,
    idsToFetch,
    isCountsOwner,
    isProjectId,
    isWatchEventForUser,
    MAX_COUNT_PROJECTS,
    mergeProjectCounts,
    nextAsOfMark,
    normalizeProjectIds,
    planAnswer,
    ProjectConversationCounts,
    ProjectCountsMap,
    reconnectDelayMs,
    setProjectCounts,
    toRequestSeq,
    unionRegistered,
} from './conversation-counts.rules';
import { CONVERSATIONS_PERMISSION } from './conversations-overlay.service';

export type { CountsOwner, ProjectConversationCounts } from './conversation-counts.rules';

/**
 * The scheduler the store's timers (`auditTime`, the start-up call, the reconnect delay) and its circuit-breaker
 * clock run on: RxJS's `asyncScheduler` (real time), unless a spec provides a virtual one.
 */
export const CONVERSATION_COUNTS_SCHEDULER = new InjectionToken<SchedulerLike>('CONVERSATION_COUNTS_SCHEDULER', {
    providedIn: 'root',
    factory: () => asyncScheduler,
});

/**
 * How a `GetCounts` call ended: its answer applied (wholly or in part); dropped as `superseded` (requested before an
 * optimistic change or an applied read-state answer: asked again); `stale` (every target it carried already holds a
 * newer answer: nothing owed); or failed (the breaker opens).
 */
type CountsCallOutcome = 'applied' | 'superseded' | 'stale' | 'failed';

/** `projects$` before any answer. Never mutated: every change publishes a new map. */
const NO_PROJECT_COUNTS: ProjectCountsMap = new Map<number, ProjectConversationCounts>();

/**
 * `ConversationCountsStore` — the one source of the sidebar Conversations badge, the Projects-list chips and the
 * project tab dots (GLOBAL_CONVERSATIONS_SLICE2_SPEC.md §6.3, SD2-11, §4.6, SD2-30).
 *
 * Lifecycle: lazy. It starts with the first subscriber of `badge$` or `projects$` (one shared lifecycle for both, so
 * a component reading both makes one start-up call) and stops with the last, and only when the user holds
 * `Pages.Agenda` (SD2-24): without it there is no call and no SignalR subscription, and `badge$` stays hidden. A start
 * makes one call at once (badge plus the registered ids); a restart refetches every known id.
 *
 * Triggers (§4.6): `UserSignalrService.conversationCountsChanged` (already tenant- and own-tab-echo-filtered),
 * `taskWatched` / `taskUnwatched` for the session user, and `refresh()`, coalesced by `auditTime(750)`; a reconnect
 * (`connectionReestablished`) after a random 3-10 s. Each refresh makes every registered id stale. Calls go through
 * `exhaustMap` (one in flight per tab); what arrives meanwhile is sent by the next call. Owners register the ids they
 * show (`watchProjects`); ids new to the store, or stale, are fetched once registration settles. EVERY call sends
 * `includeBadge: true` and at most 500 ids (SD2-11: a badge-free call costs the same).
 *
 * Answers (§6.3): a requested id missing from a `GetCounts` answer is stored as not accessible; read-state answers are
 * ordered by the client request sequence; any answer requested before the latest optimistic change is dropped. The
 * store enforces the sequence rules itself, for its own calls and for every answer handed to `applyCounts` /
 * `applyReadStateResult` / `applyFeedCounts` with the `requestSeq` the caller took from `nextRequestSeq()` when it sent
 * the request. The `AsOfUtc` rule is kept per target: the badge and each project keep the `AsOfUtc` of their last
 * applied answer, and an answer's badge or project row older than that target's mark is skipped while its other
 * targets apply (`planAnswer`), so a page-1 probe and a `GetCounts` racing from the same push do not drop each other.
 *
 * Reconcile: a dropped answer leaves nothing behind. The store's own `GetCounts` dropped as superseded is asked again
 * through the 750 ms audit. An answer handed over and dropped as superseded (or a read-state answer dropped as stale)
 * is reconciled the same way: its projects are fetched again and, when it carried the badge, one call is owed
 * (`droppedAnswerNeedsReconcile`), because this tab gets no push for its own read-state writes (SD2-10). A counts or
 * feed answer whose every target holds a newer answer needs nothing.
 *
 * Errors are swallowed (ABP's interceptor already shows its modal) with no retry, and after a failed call the store
 * makes no background call for 60 s (the circuit breaker). Every state change is emitted inside `NgZone.run`: the
 * SignalR subjects fire outside Angular's zone.
 *
 * A typed root service, never a `DataService` channel.
 */
@Injectable({ providedIn: 'root' })
export class ConversationCountsStore implements OnDestroy {
    private readonly destroy$ = new Subject<void>();
    private readonly badgeCount = new BehaviorSubject<number | null>(null);
    private readonly projectCounts = new BehaviorSubject<ProjectCountsMap>(NO_PROJECT_COUNTS);
    private readonly auditKicks = new Subject<void>();
    private readonly settleKicks = new Subject<void>();
    private readonly registrations = new Map<CountsOwner, number[]>();
    private readonly fetchedGen = new Map<number, number>();
    private stop$: Subject<void> | null = null;
    private running = false;
    private destroyed = false;
    /** Bumped by every refresh (push, watch event, `refresh()`, reconnect, start): ids fetched before it are stale. */
    private refreshGen = 0;
    /** The `refreshGen` the latest call was sent for: lower than `refreshGen` while a refresh is still owed. */
    private requestedGen = 0;
    /** The client sequence: each request, optimistic change and applied read-state answer takes the next number. */
    private seq = 0;
    /** The sequence of the latest optimistic change that moved the badge. */
    private lastOptimisticSeq = 0;
    /** The request sequence of the newest applied read-state answer that carried one. */
    private lastReadStateSeq = 0;
    /** The sequence at which the last read-state answer was applied. */
    private lastReadStateAppliedSeq = 0;
    /** The `AsOfUtc` (ms) of the last applied badge (a `GetCounts` or All-scope feed answer). */
    private badgeAsOfMs: number | null = null;
    /** The `AsOfUtc` (ms) of each project's last applied counts (a `GetCounts` row, a project-scope feed answer). */
    private readonly projectAsOfMs = new Map<number, number>();
    private lastFailureAt: number | null = null;

    /** Subscribed by both public streams: its first subscriber starts the store, its last one stops it. */
    private readonly active$: Observable<never> = new Observable<never>(() => {
        this.start();
        return () => this.stop();
    }).pipe(share());

    /** The sidebar badge: hidden at once, then the server's `NavBadgeCount` (99+ capped, hidden at 0 or unknown). */
    readonly badge$: Observable<ConversationBadgeVm> = merge(
        this.active$,
        this.badgeCount.pipe(
            distinctUntilChanged(),
            map((count: number | null) => toBadgeVm(count))
        )
    ).pipe(shareReplay({ bufferSize: 1, refCount: true }));

    /**
     * Per-project counts by project id: an empty map at once, then every answered project. A project with no entry
     * has not been answered yet (unknown, not "not accessible").
     */
    readonly projects$: Observable<ReadonlyMap<number, ProjectConversationCounts>> = merge(
        this.active$,
        this.projectCounts.asObservable()
    ).pipe(shareReplay({ bufferSize: 1, refCount: true }));

    constructor(
        private readonly _proxy: ConversationServiceProxy,
        private readonly _signalr: UserSignalrService,
        private readonly _zone: NgZone,
        private readonly _session: AppSessionService,
        private readonly _permissionChecker: PermissionCheckerService,
        @Inject(CONVERSATION_COUNTS_SCHEDULER) private readonly _scheduler: SchedulerLike
    ) {}

    /**
     * The badge's last known count (`NavBadgeCount`, with any optimistic delta applied): the unfiltered "Needs my
     * attention" count (INV-1). Null while unknown (before the first answer, or without `Pages.Agenda`). The overlay
     * reads it at open to pick its first tab (owner 2026-10-21, `allScopeEntry`).
     */
    get currentBadgeCount(): number | null {
        return this.badgeCount.value;
    }

    /**
     * Register the project ids `owner` shows (replacing its previous ones). Ids new to the store, or stale since the
     * last refresh, are fetched once registration settles; ids already fresh are not sent again.
     */
    watchProjects(owner: 'project-list' | 'project-page' | 'overlay', projectIds: readonly number[]): void {
        const ids = normalizeProjectIds(projectIds);

        if (!isCountsOwner(owner)) {
            return;
        }
        this.registrations.set(owner, ids);
        if (this.running && idsToFetch(ids, this.fetchedGen, this.refreshGen).length > 0) {
            this.settleKicks.next();
        }
    }

    /** Forget `owner`'s ids. Their counts stay in `projects$`: another owner or a later registration may use them. */
    unwatchProjects(owner: 'project-list' | 'project-page' | 'overlay'): void {
        this.registrations.delete(owner);
    }

    /**
     * Refetch the badge and every registered id (through the 750 ms audit). Every id known so far becomes stale, so an
     * id registered again later is fetched again. A background call: none while the breaker is open.
     */
    refresh(): void {
        this.markStale();
        if (this.running) {
            this.auditKicks.next();
        }
    }

    /**
     * A new client request sequence (a whole number ≥ 1, never repeated). Take one for every request whose answer
     * will be handed to `applyCounts`, `applyReadStateResult` or `applyFeedCounts`, right when the request is sent and
     * AFTER that action's own `applyOptimisticBadgeDelta`, and pass it back as the answer's `requestSeq`. The store
     * then drops the answer when an optimistic change (or, for a counts or feed answer, an applied read-state answer)
     * came after the request, orders read-state answers by it (§6.3), and reconciles a dropped answer's badge and
     * projects with its own `GetCounts` (the caller does nothing more).
     */
    nextRequestSeq(): number {
        this.seq++;
        return this.seq;
    }

    /**
     * A `GetCounts` answer from elsewhere: its badge and its rows, unless it is superseded (see `nextRequestSeq`);
     * a target (the badge, a project) whose last applied `AsOfUtc` is newer keeps its value. Its request's ids are not
     * known, so no id is marked not accessible. Without `requestSeq` only the `AsOfUtc` rule applies. Dropped as
     * superseded: reconciled by the store's own `GetCounts`.
     */
    applyCounts(dto: ConversationCountsDto, requestSeq?: number | null): void {
        let verdict: CountsAnswerVerdict | null = null;

        if (!dto) {
            return;
        }
        verdict = this.applyCountsAnswer(dto, null, toRequestSeq(requestSeq), null);
        this.reconcileIfDropped(
            'counts',
            verdict,
            answerProjectIds(dto.projects, null),
            dto.navBadgeCount !== undefined
        );
    }

    /**
     * A read-state answer (Mark read / unread / all read, the ambient auto-read, Undo): its `NavBadgeCount` and the
     * accessible projects of the affected entries. It carries no `AsOfUtc`: pass the `requestSeq` taken when the
     * request was sent, so an answer requested before the latest optimistic change, or before a read-state answer
     * already applied, is dropped. Without it, answers are applied in the order they are handed over. An applied one
     * supersedes every `GetCounts` and feed answer requested before it. A dropped one is reconciled by the store's own
     * `GetCounts` (its projects, and the badge when it carried one): this tab gets no push for it (SD2-10).
     */
    applyReadStateResult(result: ConversationReadStateResultDto, requestSeq?: number | null): void {
        const answerSeq = toRequestSeq(requestSeq);
        const stamp: CountsAnswerStamp = { kind: 'readState', asOfUtc: null, requestSeq: answerSeq };
        let carriesBadge = false;
        let projectIds: number[] = [];
        let plan: CountsAnswerPlan | null = null;

        if (!result) {
            return;
        }
        carriesBadge = result.navBadgeCount !== undefined;
        projectIds = answerProjectIds(result.projects, null);
        plan = planAnswer(stamp, this.sequenceState(), this.asOfMarks(), carriesBadge, projectIds);
        if (plan.verdict !== 'current') {
            this.reconcileIfDropped('readState', plan.verdict, projectIds, carriesBadge);
            return;
        }
        this.lastReadStateAppliedSeq = this.nextRequestSeq();
        if (answerSeq !== null) {
            this.lastReadStateSeq = Math.max(this.lastReadStateSeq, answerSeq);
        }
        this.publish(
            plan.badge ? badgeFromAnswer(this.badgeCount.value, result.navBadgeCount) : this.badgeCount.value,
            mergeProjectCounts(this.projectCounts.value, result.projects, null)
        );
    }

    /**
     * The tab counts of a feed answer (a counts probe or page 1; never a refresh by key), unless it is superseded (see
     * `nextRequestSeq`) or its target already holds a newer `AsOfUtc`. All scope (`projectId` null): the badge from
     * `counts.navBadgeCount`. Project scope: that project's counts (`AttentionCount` → unread, `AllCount` → entries);
     * pass the answer's `projectAccessible` so a refused project (`false`) is stored as not accessible (null or
     * omitted: accessible). Dropped as superseded: reconciled by the store's own `GetCounts`.
     */
    applyFeedCounts(
        projectId: number | null,
        counts: ConversationTabCountsDto,
        asOfUtc: string,
        projectAccessible?: boolean | null,
        requestSeq?: number | null
    ): void {
        const answerSeq = toRequestSeq(requestSeq);
        const stamp: CountsAnswerStamp = { kind: 'feed', asOfUtc: asOfUtc || null, requestSeq: answerSeq };
        const allScope = projectId === null || projectId === undefined;
        let carriesBadge = false;
        let projectIds: number[] = [];
        let plan: CountsAnswerPlan | null = null;

        if (!counts || (!allScope && !isProjectId(projectId))) {
            return;
        }
        carriesBadge = allScope && counts.navBadgeCount !== undefined;
        projectIds = allScope ? [] : [projectId];
        plan = planAnswer(stamp, this.sequenceState(), this.asOfMarks(), carriesBadge, projectIds);
        if (plan.verdict !== 'current') {
            this.reconcileIfDropped('feed', plan.verdict, projectIds, carriesBadge);
            return;
        }
        this.markApplied(plan, asOfUtc);
        this.publish(
            plan.badge ? badgeFromAnswer(this.badgeCount.value, counts.navBadgeCount) : this.badgeCount.value,
            plan.projectIds.length > 0
                ? setProjectCounts(
                      this.projectCounts.value,
                      feedProjectCounts(projectId, counts, projectAccessible !== false)
                  )
                : this.projectCounts.value
        );
    }

    /**
     * An optimistic badge change before the server answers (Mark read −1, Mark unread +1, Undo +1; Mark all read: a
     * delta that reaches 0, e.g. `-Infinity`). Never below 0; an unknown (hidden) badge stays hidden. A change that
     * moves the badge drops every answer requested before it; one that moves nothing (unknown badge, already 0) drops
     * nothing.
     */
    applyOptimisticBadgeDelta(delta: number): void {
        const next = applyBadgeDelta(this.badgeCount.value, delta);

        if (next === this.badgeCount.value) {
            return;
        }
        this.lastOptimisticSeq = this.nextRequestSeq();
        this.publish(next, this.projectCounts.value);
    }

    ngOnDestroy(): void {
        this.destroyed = true;
        this.destroy$.next();
        this.destroy$.complete();
        this.stop();
    }

    /** The first subscriber arrived. A no-op without `Pages.Agenda` (no call, no SignalR subscription) or destroyed. */
    private start(): void {
        let stop$: Subject<void> | null = null;

        if (this.running || this.destroyed || !this._permissionChecker.isGranted(CONVERSATIONS_PERMISSION)) {
            return;
        }
        stop$ = new Subject<void>();
        this.stop$ = stop$;
        this.running = true;
        this.markStale();
        merge(timer(0, this._scheduler), this.auditedTriggers(), this.settledTriggers(), this.reconnectTriggers())
            .pipe(
                filter(() => this.callNeeded()),
                exhaustMap(() => this.fetchCounts()),
                takeUntil(stop$),
                takeUntil(this.destroy$)
            )
            .subscribe();
    }

    /** The last subscriber left: tear every subscription down (an in-flight call is cancelled). The counts are kept. */
    private stop(): void {
        const stop$ = this.stop$;

        if (!this.running) {
            return;
        }
        this.running = false;
        this.stop$ = null;
        if (stop$) {
            stop$.next();
            stop$.complete();
        }
    }

    /** Pushes and the session user's watch events (each a refresh), refresh() and follow-ups: via `auditTime(750)`. */
    private auditedTriggers(): Observable<unknown> {
        const pushes$ = merge(this._signalr.conversationCountsChanged, this.watchEventsForMe()).pipe(
            tap(() => this.markStale())
        );

        return merge(pushes$, this.auditKicks).pipe(auditTime(COUNTS_AUDIT_MS, this._scheduler));
    }

    /** Registrations, and the ids a call could not carry, once quiet for `COUNTS_SETTLE_MS`. */
    private settledTriggers(): Observable<void> {
        return this.settleKicks.pipe(auditTime(COUNTS_SETTLE_MS, this._scheduler));
    }

    /** One refresh 3-10 s after a reconnect; a later reconnect restarts the wait (they never stack). */
    private reconnectTriggers(): Observable<number> {
        return this._signalr.connectionReestablished.pipe(
            switchMap(() => timer(reconnectDelayMs(Math.random()), this._scheduler)),
            tap(() => this.markStale())
        );
    }

    /** `taskWatched` / `taskUnwatched` about the session user only (another user's watch changes nothing here). */
    private watchEventsForMe(): Observable<unknown> {
        return merge(this._signalr.taskWatched, this._signalr.taskUnwatched).pipe(
            filter((payload: unknown) => isWatchEventForUser(payload, this._session.userId))
        );
    }

    /** Whether a call is owed (a refresh not yet sent, or registered ids to fetch) and allowed (breaker closed). */
    private callNeeded(): boolean {
        if (!this.running || breakerOpen(this.lastFailureAt, this._scheduler.now())) {
            return false;
        }
        return (
            this.requestedGen < this.refreshGen ||
            idsToFetch(this.registeredIds(), this.fetchedGen, this.refreshGen).length > 0
        );
    }

    /** One `GetCounts`: the badge and at most 500 ids to fetch. Never errors (the outer pipeline must survive). */
    private fetchCounts(): Observable<ConversationCountsDto> {
        const gen = this.refreshGen;
        const requestSeq = this.nextRequestSeq();
        const projectIds =
            chunkIds(idsToFetch(this.registeredIds(), this.fetchedGen, gen), MAX_COUNT_PROJECTS)[0] || [];
        const input = new GetConversationCountsInput({
            includeBadge: true,
            projectIds: projectIds.length > 0 ? projectIds : undefined,
        });
        let outcome: CountsCallOutcome | null = null;

        this.requestedGen = gen;
        return defer(() => this._proxy.getCounts(input)).pipe(
            tap((dto: ConversationCountsDto) => {
                outcome = this.onCountsAnswer(dto, projectIds, gen, requestSeq);
            }),
            catchError(() => {
                outcome = this.onCountsFailure();
                return EMPTY;
            }),
            finalize(() => this.afterCall(outcome, gen))
        );
    }

    private onCountsAnswer(
        dto: ConversationCountsDto,
        projectIds: readonly number[],
        gen: number,
        requestSeq: number
    ): CountsCallOutcome {
        let verdict: CountsAnswerVerdict | null = null;

        if (!dto) {
            return this.onCountsFailure();
        }
        verdict = this.applyCountsAnswer(dto, projectIds, requestSeq, gen);
        return verdict === 'current' ? 'applied' : verdict;
    }

    /** No retry: the breaker opens, and the next trigger after it closes calls again. */
    private onCountsFailure(): CountsCallOutcome {
        this.lastFailureAt = this._scheduler.now();
        return 'failed';
    }

    /**
     * After a call: what arrived meanwhile goes in the next call. A refresh owed (including one a dropped answer left
     * owed) waits for the 750 ms audit, so two push-driven calls never start within 750 ms; ids still to fetch go
     * after the settle. Nothing after a failure. A `superseded` answer is asked again (each one follows a caller's
     * change, so it cannot loop); a `stale` one owes nothing (every target it carried holds a newer answer), so
     * disagreeing server clocks cannot loop either.
     */
    private afterCall(outcome: CountsCallOutcome | null, gen: number): void {
        if (!this.running || outcome === null) {
            return;
        }
        switch (outcome) {
            case 'failed':
                return;
            case 'superseded':
                this.askAgain(gen);
                return;
            case 'stale':
            case 'applied':
                if (this.requestedGen < this.refreshGen) {
                    this.auditKicks.next();
                } else if (this.callNeeded()) {
                    this.settleKicks.next();
                }
                return;
            default:
                unknownCallOutcome(outcome);
        }
    }

    /** A dropped answer: the refresh it was sent for is owed again, and the next call goes after the 750 ms audit. */
    private askAgain(gen: number): void {
        this.requestedGen = Math.min(this.requestedGen, gen - 1);
        this.auditKicks.next();
    }

    /**
     * An answer a caller handed over was dropped. When `droppedAnswerNeedsReconcile` says nothing newer covers it
     * (superseded; a read-state answer dropped as stale), reconcile through the store's own `GetCounts`: its projects
     * are fetched again (now if registered, else when next registered) and, when it carried the badge, one call is
     * owed. Via the 750 ms audit; nothing while the breaker is open (`callNeeded`). At most one call per dropped
     * answer, coalesced with every other trigger; the call takes a new request sequence, so it converges.
     */
    private reconcileIfDropped(
        kind: CountsAnswerKind,
        verdict: CountsAnswerVerdict,
        projectIds: readonly number[],
        carriesBadge: boolean
    ): void {
        if (!droppedAnswerNeedsReconcile(kind, verdict) || (!carriesBadge && projectIds.length === 0)) {
            return;
        }
        projectIds.forEach((projectId) => this.fetchedGen.delete(projectId));
        if (carriesBadge) {
            this.requestedGen = Math.min(this.requestedGen, this.refreshGen - 1);
        }
        if (this.running) {
            this.auditKicks.next();
        }
    }

    /**
     * Apply what `planAnswer` lets through of a `GetCounts` answer: its badge, and the projects whose mark is not
     * newer. `requestedIds` (the store's own calls) marks the requested ids the answer left out as not accessible and
     * records every requested id as fetched in `gen`, before the emission: a project skipped for a newer mark holds a
     * value counted after this answer, so it is fresh too. A superseded answer records nothing (it is asked again).
     */
    private applyCountsAnswer(
        dto: ConversationCountsDto,
        requestedIds: readonly number[] | null,
        requestSeq: number | null,
        gen: number | null
    ): CountsAnswerVerdict {
        const stamp: CountsAnswerStamp = { kind: 'counts', asOfUtc: dto.asOfUtc || null, requestSeq };
        const carriesBadge = dto.navBadgeCount !== undefined;
        const plan = planAnswer(
            stamp,
            this.sequenceState(),
            this.asOfMarks(),
            carriesBadge,
            answerProjectIds(dto.projects, requestedIds)
        );
        const keep = new Set<number>(plan.projectIds);

        if (plan.verdict === 'superseded') {
            return plan.verdict;
        }
        if (requestedIds && gen !== null) {
            requestedIds.forEach((projectId) => this.fetchedGen.set(projectId, gen));
        }
        if (plan.verdict === 'stale') {
            return plan.verdict;
        }
        this.markApplied(plan, dto.asOfUtc);
        this.publish(
            plan.badge ? badgeFromAnswer(this.badgeCount.value, dto.navBadgeCount) : this.badgeCount.value,
            mergeProjectCounts(
                this.projectCounts.value,
                (dto.projects || []).filter((row) => !!row && keep.has(row.projectId)),
                requestedIds ? requestedIds.filter((projectId) => keep.has(projectId)) : null
            )
        );
        return plan.verdict;
    }

    /** Move the `AsOfUtc` mark of every target `plan` applied (never back; an unreadable time moves nothing). */
    private markApplied(plan: CountsAnswerPlan, asOfUtc: string | null | undefined): void {
        if (plan.badge) {
            this.badgeAsOfMs = nextAsOfMark(this.badgeAsOfMs, asOfUtc);
        }
        plan.projectIds.forEach((projectId) => {
            const last = this.projectAsOfMs.has(projectId) ? this.projectAsOfMs.get(projectId) : null;
            const mark = nextAsOfMark(last, asOfUtc);

            if (mark !== null) {
                this.projectAsOfMs.set(projectId, mark);
            }
        });
    }

    /** Emit what changed, inside Angular's zone (the triggers and the HTTP answers may run outside it). */
    private publish(badge: number | null, projects: ProjectCountsMap): void {
        const badgeChanged = badge !== this.badgeCount.value;
        const projectsChanged = projects !== this.projectCounts.value;

        if (!badgeChanged && !projectsChanged) {
            return;
        }
        this._zone.run(() => {
            if (badgeChanged) {
                this.badgeCount.next(badge);
            }
            if (projectsChanged) {
                this.projectCounts.next(projects);
            }
        });
    }

    private sequenceState(): CountsSequenceState {
        return {
            lastReadStateSeq: this.lastReadStateSeq,
            lastOptimisticSeq: this.lastOptimisticSeq,
            lastReadStateAppliedSeq: this.lastReadStateAppliedSeq,
        };
    }

    private asOfMarks(): CountsAsOfMarks {
        return { badgeMs: this.badgeAsOfMs, projectMs: this.projectAsOfMs };
    }

    private registeredIds(): number[] {
        return unionRegistered(this.registrations);
    }

    private markStale(): void {
        this.refreshGen++;
    }
}

/** Compile-time exhaustiveness of `CountsCallOutcome`; at run time an unknown outcome does nothing. */
function unknownCallOutcome(_outcome: never): void {
    return;
}
